/**
 * 路由分類與 locale 解析 —— 認證跳轉邏輯的單一來源
 *
 * auth.global 與 redirect.global 過去各自帶了一份 parseLocaleFromPath，
 * 兩份對「不合法 locale」的處理還不一致。所有跟「這條路徑屬於哪一類」有關的判斷
 * 都集中在這裡，middleware 只負責決策、不負責解析。
 */

export const SUPPORTED_LOCALES = ['en', 'zh-TW'] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/**
 * Google OAuth 的回調中繼頁
 *
 * 這條路徑上有一件別處沒有的事：後端寫在父網域上的認證 cookie 還沒交接給前端，
 * 任何「順手清掉舊名稱 cookie」的動作都必須避開它
 * （見 `plugins/00.authCookieMigration.client.ts`）。
 */
export const AUTH_CALLBACK_PATH = 'auth/callback';

/**
 * 公開路徑：未登入也能訪問
 */
export const PUBLIC_PATHS = ['login', 'signup', 'forgot-password', AUTH_CALLBACK_PATH] as const;

/**
 * 訪客專用路徑：已經登入且組織就緒後就不該再停留，會被導向 dashboard
 *
 * 注意 organization/setup 不在這裡 —— 它有自己的規則（見 isOrgSetupPath），
 * 因為「沒有組織」時它是唯一該去的地方，「已有組織」時才該離開。
 */
export const GUEST_ONLY_PATHS = ['login', 'signup', 'forgot-password'] as const;

/** 組織設定頁 */
export const ORG_SETUP_PATH = 'organization/setup';

/**
 * 「我是來建立另一個組織的」意圖旗標
 *
 * 組織設定頁預設只留給「還沒有組織」的人，已有組織的人一進來就會被送回 dashboard。
 * 但已有組織的人也需要能再開一個組織，走的又是同一套 wizard —— 所以用這個 query
 * 把「被系統推來的」和「自己主動要來建組織的」區分開，後者才放行。
 *
 * 一旦組織建立成功就會被清掉（見 OrganizationSetupWizard），
 * 避免重新整理時又回到「輸入組織名稱」而重複建立。
 */
export const ORG_SETUP_INTENT_QUERY = 'intent';
export const ORG_SETUP_CREATE_INTENT = 'create';

/** 這次導航是不是帶著「建立新組織」的意圖 */
export const isCreateOrganizationIntent = (
  query: Record<string, unknown> | undefined | null
): boolean => {
  const value = query?.[ORG_SETUP_INTENT_QUERY];
  // query 值可能是陣列（同一個 key 帶多次），取第一個即可
  const intent = Array.isArray(value) ? value[0] : value;
  return intent === ORG_SETUP_CREATE_INTENT;
};

/** Chrome DevTools 的探測請求，直接放行 */
export const DEVTOOLS_PROBE_PATH = '/.well-known/appspecific/com.chrome.devtools.json';

/**
 * 解析路徑中的語系，並回傳去掉語系前綴後的路徑
 *
 * @returns locale 為 null 表示路徑沒有（或帶著不支援的）語系前綴
 */
export const parseLocaleFromPath = (
  path: string
): { locale: SupportedLocale | null; pathWithoutLocale: string } => {
  const pathWithoutLeadingSlash = path.replace(/^\/+/, '');

  // 匹配語系部分（確保語系後面有斜線或結尾）
  const localeMatch = pathWithoutLeadingSlash.match(/^([a-z]{2}(-[a-z]{2})?)(\/|$)/i);
  const matchedLocale = localeMatch?.[1];

  if (!matchedLocale || !SUPPORTED_LOCALES.includes(matchedLocale as SupportedLocale)) {
    return { locale: null, pathWithoutLocale: path };
  }

  const pathWithoutLocale = pathWithoutLeadingSlash.replace(/^[a-z]{2}(-[a-z]{2})?(\/|$)/i, '');
  return {
    locale: matchedLocale as SupportedLocale,
    pathWithoutLocale: pathWithoutLocale ? `/${pathWithoutLocale}` : '/',
  };
};

/**
 * 標準化路徑：轉小寫、去掉頭尾斜線，方便與上面的常數比對
 *
 * 結尾斜線一定要去掉。`router.options.strict: false` 讓 `/login/` 也對得到登入頁，
 * 但沒去掉的話 `toComparablePath('/login/')` 會是 `'login/'`，於是它既不是 public
 * 也不是 guest-only —— 未登入的人多繞一次跳轉，已登入的人則會直接卡在登入頁。
 */
export const normalizePath = (path: string): string =>
  path.toLowerCase().replace(/^\/+/, '').replace(/\/+$/, '');

/** 把 route path 一次轉成可比對的形式（去語系 + 標準化） */
export const toComparablePath = (path: string): string =>
  normalizePath(parseLocaleFromPath(path).pathWithoutLocale);

export const isPublicPath = (comparablePath: string): boolean =>
  PUBLIC_PATHS.includes(comparablePath as (typeof PUBLIC_PATHS)[number]);

export const isGuestOnlyPath = (comparablePath: string): boolean =>
  GUEST_ONLY_PATHS.includes(comparablePath as (typeof GUEST_ONLY_PATHS)[number]);

/**
 * 組織設定頁**本身**（不含子路徑）
 *
 * 用在「已經有組織的人不該再停留於此」那條規則上。刻意不含子路徑：
 * `organization/setup/processing` 是付款完成後的回導頁，走到那裡時組織其實已經建好、
 * 也已經是 current 了，它要在原地把付款結果輪詢完才會自己導回設定頁
 * （見 pages/organization/setup/processing/_components/Processing.vue）。
 * 把它一起算進來，使用者一回站就被送去 dashboard，看不到付款結果與後面的邀請步驟。
 */
export const isOrgSetupPath = (comparablePath: string): boolean =>
  comparablePath === ORG_SETUP_PATH;

/**
 * 組織設定**流程**的任一頁（設定頁本身與其子路徑）
 *
 * 用在「還沒有組織的人可以待在這裡」那條規則上。這裡要含子路徑：不含的話，還沒換到新
 * 組織 scope 的使用者一落在 `organization/setup/processing` 就會被踢回設定頁的第一步，
 * 剛付完款的流程直接斷掉。
 *
 * 與 `isOrgSetupPath` 的差別就是「可以留」與「該離開」是兩個不同的問題，
 * 對子路徑的答案剛好相反。
 */
export const isOrgSetupFlowPath = (comparablePath: string): boolean =>
  comparablePath === ORG_SETUP_PATH || comparablePath.startsWith(`${ORG_SETUP_PATH}/`);

// ---------------------------------------------------------------------------
// 手機版
// ---------------------------------------------------------------------------

/** 手機版首頁：對話清單（平台小助理 + 自己的 Agent） */
export const MOBILE_HOME_PATH = 'chats';

/**
 * 手機版**只**放行這些頁面，其餘一律導回 `MOBILE_HOME_PATH`。
 *
 * 手機版的設計是「除了登入登出、切換組織，其他都透過小助理對話完成」，所以沒有把每一頁
 * 都做 RWD 的打算；能在手機出現的頁面是刻意挑過的：
 *   - 對話面：對話清單、平台小助理、單一 Agent 的對話
 *   - 帳號：設定頁（手機只顯示帳號分頁）
 *   - 信件深連結會落到的頁：帳單下載
 *   - 認證與組織設定流程另由 `isPublicPath` / `isOrgSetupFlowPath` 放行（邀請、付款回跳都在那裡）
 */
const MOBILE_ALLOWED_EXACT_PATHS = [
  MOBILE_HOME_PATH,
  `${MOBILE_HOME_PATH}/assistant`,
  'settings',
  'billing/download',
] as const;

/** `agents/<uuid>/chat` —— 單一 Agent 的對話頁 */
const MOBILE_AGENT_CHAT_PATTERN = /^agents\/[^/]+\/chat$/;

export const isMobileAllowedPath = (comparablePath: string): boolean =>
  isPublicPath(comparablePath) ||
  isOrgSetupFlowPath(comparablePath) ||
  MOBILE_ALLOWED_EXACT_PATHS.includes(
    comparablePath as (typeof MOBILE_ALLOWED_EXACT_PATHS)[number]
  ) ||
  MOBILE_AGENT_CHAT_PATTERN.test(comparablePath);

/** 是否為 Google OAuth 的回調中繼頁 */
export const isAuthCallbackPath = (comparablePath: string): boolean =>
  comparablePath === AUTH_CALLBACK_PATH;

/** 是否為根路徑（`/` 或 `/zh-TW`），這類路徑會被導向 dashboard */
export const isRootPath = (path: string): boolean => {
  const { pathWithoutLocale } = parseLocaleFromPath(path);
  return pathWithoutLocale === '/' || pathWithoutLocale === '';
};
